import type {
    FigureCell,
    FigureDefinition,
    FigureInline,
    FigureMoneyCell,
    FigureRangeCell,
    FigureRecordCell,
    FigureRows,
    FigureRowsColumn,
} from 'types/aiChatFigure';
import { isGlobalSearchEntityType } from 'types/globalSearch';
import { absoluteUrlForResult, resolveRecordFromPath, UNSEARCHED_RECORD_SEGMENT } from 'utils/globalSearchRoutes';
import { csvNumber, type ChatTable, type TypedCell } from '../../../utils/tableExport';
import { resolveAiChatLink } from '../aiChatLinkResolver';
import { columnLabel } from './figureMeta';

const isObject = (cell: FigureCell): cell is FigureRecordCell | FigureMoneyCell | FigureRangeCell =>
    typeof cell === 'object' && cell !== null && !Array.isArray(cell);

export const isMoneyCell = (cell: FigureCell): cell is FigureMoneyCell => isObject(cell) && 'amount' in cell;

/** A footer's range: the lowest and the highest value of its column. */
export const isRangeCell = (cell: FigureCell): cell is FigureRangeCell => isObject(cell) && 'min' in cell;

/** One end of a range as a cell of its column: a money end takes the range's currency. */
export const rangeEnd = (range: FigureRangeCell, column: FigureRowsColumn, value: number): FigureCell =>
    column.type === 'money' ? { amount: value, currency: range.currency ?? null } : value;

/** An empty cell of a column whose empty value was never entered: it reads "Not set" on screen and in a file alike. */
export const isNotSet = (cell: FigureCell, column: FigureRowsColumn): boolean =>
    column.notSet === true && (cell === null || cell === '');

/** A record cell this build can open: a search family, a task or a training, and a numeric id. */
export const recordOf = (
    cell: FigureCell
): { entityType: FigureRecordCell['entityType']; id: number; label: string } | null => {
    if (
        !isObject(cell) ||
        !('entityType' in cell) ||
        !(isGlobalSearchEntityType(cell.entityType) || Object.hasOwn(UNSEARCHED_RECORD_SEGMENT, cell.entityType))
    ) {
        return null;
    }
    const id = Number(cell.id);
    return Number.isSafeInteger(id) ? { entityType: cell.entityType, id, label: cell.label ?? '' } : null;
};

/** A cell's text where the Figure cannot draw it as what it is; several records' names joined by ", ". */
export const cellText = (cell: FigureCell): string => {
    if (cell === null) {
        return '';
    }
    if (Array.isArray(cell)) {
        return cell.map(cellText).filter(Boolean).join(', ');
    }
    if (isMoneyCell(cell)) {
        return `${cell.amount}${cell.currency ? ` ${cell.currency}` : ''}`;
    }
    if (isRangeCell(cell)) {
        return `${cell.min} – ${cell.max}`;
    }
    return isObject(cell) ? (cell.label ?? '') : String(cell);
};

// One record link, the whole cell: `[[O002] Orion Group](crespect://contact/12)`.
const RECORD_LINK = /^\[(.*)\]\(([^()\s]+)\)$/s;

/** An inline table's text cell: its record link as a record cell, any other text as it is. */
export const inlineTextCell = (text: string): FigureCell => {
    const [, label, href] = RECORD_LINK.exec(text.trim()) ?? [];
    const resolution = href ? resolveAiChatLink(href) : null;
    const record =
        resolution && (resolution.kind === 'internal' || resolution.kind === 'record')
            ? resolveRecordFromPath(resolution.to)
            : null;
    return record ? { ...record, label: label ?? '' } : text;
};

/**
 * An inline table's rows and footer rows in the shape the Figure endpoint reads a source's in: the AI wrote them, so
 * nothing is read again. A money column's numbers and ranges take its currency, a text cell holding a record link is
 * that record.
 */
export const inlineFigureRows = (definition: FigureDefinition): FigureRows | null => {
    const { columns, inline, asOf } = definition;
    if (definition.kind !== 'table' || definition.source || !columns || !inline?.rows) {
        return null;
    }
    const cellsOf = (row: NonNullable<FigureInline['footer']>[number]) =>
        columns.map(({ field, type, currency }): FigureCell => {
            const cell = row[field] ?? null;
            if (type === 'money' && typeof cell === 'number') {
                return { amount: cell, currency: currency ?? null };
            }
            if (typeof cell === 'object' && cell !== null) {
                return type === 'money' ? { ...cell, currency: currency ?? null } : cell;
            }
            return typeof cell === 'string' && (type ?? 'text') === 'text' ? inlineTextCell(cell) : cell;
        });
    const rows = inline.rows.map(cellsOf);
    return {
        columns: columns.map(({ field, label, type }) => ({ field, label, type: type ?? 'text' })),
        rows,
        footer: inline.footer?.map(cellsOf),
        count: rows.length,
        partial: false,
        total: null,
        asOf: asOf ?? '',
    };
};

const fractionDigits = (value: number, max: number) => Math.min((String(value).split('.')[1] ?? '').length, max);

/**
 * The decimals a number or a percentage column shows on screen: the most any of its cells has, a footer's and a
 * range's ends included, up to two for a number and one for a percent. Every cell of the column then reads in one
 * form, "2.0" under "7.6". None for a column of another type.
 */
export const columnDecimals = (column: FigureRowsColumn, cells: FigureCell[]): number | undefined => {
    const max = { number: 2, percentage: 1 }[column.type as string];
    if (max === undefined) {
        return undefined;
    }
    const values = cells.flatMap((cell) => {
        if (isRangeCell(cell)) {
            return [cell.min, cell.max];
        }
        return typeof cell === 'number' && Number.isFinite(cell) ? [cell] : [];
    });
    // A share is shown as its percent: 0.378 is 37.8.
    const shown = (value: number) => (column.type === 'percentage' ? Math.round(value * 1000) / 10 : value);
    return Math.max(0, ...values.map((value) => fractionDigits(shown(value), max)));
};

const numberCell = (value: number, decimals: number, unit = '', grouped = false): TypedCell => ({
    type: 'number',
    text: `${value.toFixed(decimals)}${unit}`,
    value,
    decimals,
    grouped,
    prefix: '',
    unit,
});

// A date field holds a day, `yyyy-MM-dd`: a spreadsheet date is a day count, so it is read as UTC midnight.
const dateCell = (text: string): TypedCell => {
    const [year, month, day] = text.slice(0, 10).split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return year >= 1900 && !Number.isNaN(date.getTime()) ? { type: 'date', text, date } : { type: 'text', text };
};

const typedCell = (cell: FigureCell, column: FigureRowsColumn, notSetText: string): TypedCell => {
    if (isNotSet(cell, column)) {
        return { type: 'text', text: notSetText };
    }
    if (isRangeCell(cell)) {
        // One text cell: each end as the CSV writes its column's numbers, the row holding it after it.
        const end = (value: number, at: string | undefined) => {
            const typed = typedCell(rangeEnd(cell, column, value), column, notSetText);
            const text = typed.type === 'number' ? csvNumber(typed) : typed.text;
            return at ? `${text} (${at})` : text;
        };
        return { type: 'text', text: `${end(cell.min, cell.minAt)} – ${end(cell.max, cell.maxAt)}` };
    }
    const record = recordOf(cell);
    if (record) {
        return { type: 'text', text: record.label, href: absoluteUrlForResult(record.entityType, record.id) };
    }
    if (isMoneyCell(cell)) {
        return numberCell(Number(cell.amount), 2, cell.currency ? ` ${cell.currency}` : '', true);
    }
    if (typeof cell === 'number' && Number.isFinite(cell)) {
        switch (column.type) {
            case 'hours':
                return numberCell(cell, fractionDigits(cell, 2), ' h');
            case 'percentage': {
                const percent = Math.round(cell * 1000) / 10;
                return numberCell(percent, fractionDigits(percent, 1), '%');
            }
            case 'number':
            case 'money':
                return numberCell(cell, fractionDigits(cell, 4));
            default:
                break;
        }
    }
    if (column.type === 'date' && typeof cell === 'string') {
        return dateCell(cell);
    }
    return { type: 'text', text: cellText(cell) };
};

/**
 * The Figure's rows, then its footer rows, as the table its file and its copied CSV hold, `note` as the line under them
 * and `notSet` as an empty cell of a column whose empty value was never entered. A grouped column stays on every row,
 * for a filter.
 */
export const figureRowsTable = (
    { columns, rows, footer = [] }: FigureRows,
    note: string | null,
    notSet: string
): ChatTable<TypedCell> => ({
    headers: columns.map(columnLabel),
    rows: [...rows, ...footer].map((row) =>
        columns.map((column, index) => typedCell(row[index] ?? null, column, notSet))
    ),
    note,
});
